/**
 * The Rotom Phone's Settings: the theme, who is signed in, and the doors in and out. The phone draws no site
 * header, so what that header holds for the staff console (the theme toggle, the session, sign in and out)
 * lives here instead.
 */
import { Link } from 'react-router';

import { isStaff } from '@dimensionscore/shared';

import { useSession } from '../auth/SessionProvider';
import { SignInButton } from '../components/SessionBar';
import { ThemeToggle } from '../components/ThemeToggle';
import rotomFace from '../assets/rotom-phone-face.png';
import { useAudioCompanionState } from '../audio/AudioCompanionProvider';

export function SettingsApp(): React.JSX.Element {
  const { session, self, signOut } = useSession();
  const { state, active } = useAudioCompanionState();
  const listening = active && state.phase === 'ready' ? state.identity : null;
  const staff = session !== null && isStaff(session.claims.role);

  return (
    <div className="ph-settings" data-testid="phone-settings">
      <section className="ph-setting dc-card" aria-labelledby="ph-set-look">
        <h2 id="ph-set-look" className="ph-setting-title">
          Look
        </h2>
        <p className="ph-setting-row">
          <span>Light, dark, or as your device is set</span>
          <ThemeToggle />
        </p>
      </section>

      <section className="ph-setting dc-card" aria-labelledby="ph-set-account">
        <h2 id="ph-set-account" className="ph-setting-title">
          Account
        </h2>
        {listening !== null && (
          <p className="ph-setting-row" data-testid="settings-audio">
            <span>
              Music plays for <b>{listening.name}</b> on this device.
            </span>
          </p>
        )}
        {self !== null && listening === null && (
          <p className="ph-setting-row" data-testid="settings-self">
            <span>
              This phone knows you as <b>{self.claims.name}</b>.
            </span>
          </p>
        )}
        {session !== null || self !== null ? (
          <p className="ph-setting-row" data-testid="settings-session">
            <span>
              {session !== null ? (
                <>
                  Signed in as <b>{session.claims.name}</b>
                  {staff ? ' (staff)' : ''}.
                </>
              ) : (
                'Remembered on this device.'
              )}
            </span>
            <button type="button" className="dc-button dc-button-quiet" onClick={signOut} data-testid="sign-out">
              Sign out
            </button>
          </p>
        ) : (
          <p className="ph-setting-row">
            <span>Linked your Discord with /link? Sign in to see your stats here any time.</span>
            <SignInButton returnTo="/me" testId="settings-sign-in" />
          </p>
        )}
        {staff && (
          <p className="ph-setting-row">
            <span>The staff console: the inspector, the editor, grants and the audit log.</span>
            <Link to="/" className="dc-button" data-testid="settings-console">
              Open the console
            </Link>
          </p>
        )}
      </section>

      <section className="ph-setting dc-card ph-about" aria-labelledby="ph-set-about">
        <h2 id="ph-set-about" className="ph-setting-title">
          About
        </h2>
        <p className="ph-setting-row">
          <img src={rotomFace} width={24} height={63} alt="" className="ph-about-rotom" />
          <span>
            The CobblemonDimensions Rotom Phone. It only listens: it hears where you are in game and changes nothing there. Music credits are in the
            Music app.
          </span>
        </p>
      </section>
    </div>
  );
}
